import { useEffect, useState } from 'react';
import { NavLink, generatePath } from 'react-router-dom';
import {
  IconRight,
  // IconDown,
  // IconUp,
  // IconAttention,
  IconChat,
  ButtonSecondary,
  IconBlock,
  formatBalance,
  ClickOutsideDetector,
  ButtonPseudo,
  Popup,
  IconCloseCircle,
  PermissionEnum,
  Textarea,
  InputText,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  CustomerStatus,
  CustomerType,
  OrganizationType,
} from '../../../hoc/customers/types';
import ROUTES from '../../../routes';
import { useCloseOrganizationMutation } from '../../../hoc/customers/store/services/customers';
import { CustomerRightSideTypes } from './types';

import cn from './info-right-side.module.css';

const CustomerRightSide = ({
  customerId,
  children,
  risk,
  tariff,
  type = OrganizationType.INDIVIDUAL,
  balance,
  customerType,
  name,
  mainChat,
  status,
}: CustomerRightSideTypes) => {
  const [menuVisible, setMenuVisibile] = useState(false);
  const [resrictPopupVisible, setRestrictPopupVisible] = useState(false);
  const [customerBlockPopupVisible, setCustomerBlockPopupVisible] =
    useState(false);
  const [closePopupVisible, setClosePopupVisible] = useState(false);
  const [closePopupState, setClosePopupState] = useState<
    'warning' | 'comment' | 'name'
  >('warning');
  const [closeReason, setCloseReason] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [closeError, setCloseError] = useState('');

  const { activePermissions, riskScoreRanges, baseCurrency, currenciesList } =
    useAppSelector((state) => state.customersDictionaries);

  const [onCloseOrganization, { isLoading, isSuccess }] =
    useCloseOrganizationMutation();

  const onCloseOrganizationAction = async () => {
    setCloseError('');

    const response = (await onCloseOrganization({
      id: customerId,
      body: {
        statusChangeReason: closeReason,
      },
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setCloseError,
      });
    }
  };

  let link;

  if (customerType === CustomerType.INDIVIDUAL) {
    link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_AML, { customerId });
  } else {
    link = generatePath(ROUTES.CUSTOMERS_CORPORATE_AML, { customerId });
  }

  const riskLevel = riskScoreRanges?.find(
    (item) =>
      (risk as number) >= item.rangeStart && (risk as number) <= item.rangeEnd,
  );

  let closeOrganizationPopupTitle = 'The customer will be terminated';
  let closeOrganizationPopupDescription =
    'Accounts will be closed and customer will lose access.';
  let closeOrganizationPopupOkButtonText = 'Terminate customer';

  if (closePopupState === 'comment') {
    closeOrganizationPopupTitle = 'State the reason for closing';
    closeOrganizationPopupDescription = '';
  } else if (closePopupState === 'name') {
    closeOrganizationPopupTitle = 'Confirm the closure of the customer';
    closeOrganizationPopupDescription = '';
    closeOrganizationPopupOkButtonText = 'Confirming closure of the customer';
  }

  useEffect(() => {
    if (isSuccess) {
      setOrganizationName('');
      setClosePopupState('warning');
      setClosePopupVisible(false);
      setCloseReason('');
      setCloseError('');
    }
  }, [isSuccess]);

  return (
    <>
      <div className={cn.informationRightSideWrapper}>
        <div>
          {children}
          {type !== OrganizationType.COMPANY_OFFICIAL && (
            <>
              <NavLink
                className={cn.balanceWrapper}
                style={{ display: 'none' }}
                to={
                  customerType === CustomerType.INDIVIDUAL
                    ? generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS, {
                        customerId,
                      })
                    : generatePath(ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS, {
                        customerId,
                      })
                }
              >
                <div className={cn.balanceTitle}>
                  Total Balance
                  <IconRight className={cn.balanceIcon} />
                </div>
                <div className={cn.balanceValue}>
                  {`${
                    balance &&
                    formatBalance(
                      balance,
                      currenciesList?.find(
                        (item) => item.id === baseCurrency?.id,
                      ),
                    )
                  } ${baseCurrency?.name}`}
                  <div className={cn.balanceHint}>
                    <span>BC</span>
                  </div>
                </div>
              </NavLink>
              <NavLink to={link} className={cn.riskWrapper}>
                <div className={cn.riskTitle}>
                  {`${riskLevel?.name.toLowerCase() || 'High'} Risk`}
                  <IconRight className={cn.riskIcon} />
                </div>
                <div className={cn.riskLineWrapper}>
                  {riskScoreRanges?.map((item) => (
                    <div
                      className={`${cn.riskLine} ${
                        item.rangeStart < (riskLevel?.rangeStart as number)
                          ? cn.riskLineFilled
                          : ''
                      }`}
                      key={item.id}
                    >
                      {item.rangeStart === riskLevel?.rangeStart && (
                        <div
                          className={cn.riskLineProgress}
                          style={{
                            width: `${
                              (((risk as number) - riskLevel.rangeStart) /
                                (riskLevel.rangeEnd - riskLevel.rangeStart)) *
                              100
                            }%`,
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </NavLink>
              <NavLink
                className={cn.tariff}
                to={
                  customerType === CustomerType.INDIVIDUAL
                    ? generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS, {
                        customerId,
                      })
                    : generatePath(ROUTES.CUSTOMERS_CORPORATE_TARIFFS, {
                        customerId,
                      })
                }
              >
                {tariff}
                <IconRight className={cn.tariffIcon} />
              </NavLink>
            </>
          )}
        </div>
        <div className={cn.bottomWrapper}>
          <div className={cn.chat}>
            <NavLink
              to={
                mainChat
                  ? `/messages/main-chat/all/${mainChat.id}`
                  : `/messages/main-chat/all?name=${name}`
              }
              className={cn.chatButton}
              target="_blank"
            >
              <div className={cn.chatButtonContent}>
                <div className={cn.chatButtonContentLeft}>
                  <IconChat className={cn.chatIcon} width={24} height={24} />
                  Open chat
                </div>
                {/* {chatMessagesCount && (
                  <div className={cn.chatMessagesCount}>
                    {chatMessagesCount}
                  </div>
                )} */}
              </div>
            </NavLink>
          </div>
          {status !== CustomerStatus.CLOSED &&
            activePermissions?.includes(
              customerType === CustomerType.INDIVIDUAL
                ? PermissionEnum.INDIVIDUAL_CUSTOMERS_CLOSE_ACTION
                : PermissionEnum.CORPORATE_CUSTOMERS_CLOSE_ACTION,
            ) && (
              <button
                type="button"
                className={cn.closeOrganizationButton}
                onClick={() => setClosePopupVisible(true)}
              >
                <IconCloseCircle width={24} height={24} />
                Terminate customer
              </button>
            )}

          {activePermissions?.includes(
            customerType === CustomerType.INDIVIDUAL
              ? PermissionEnum.INDIVIDUAL_CUSTOMERS_CLOSE_ACTION ||
                  PermissionEnum.INDIVIDUAL_CUSTOMERS_UPDATE_STATUS_ACTION
              : PermissionEnum.CORPORATE_CUSTOMERS_CLOSE_ACTION ||
                  PermissionEnum.CORPORATE_CUSTOMERS_UPDATE_STATUS_ACTION,
          ) && (
            <ClickOutsideDetector setOptionsListVisible={setMenuVisibile}>
              <div className={cn.userMenuWrapper}>
                {menuVisible && (
                  <div className={cn.userMenu}>
                    {activePermissions?.includes(
                      customerType === CustomerType.INDIVIDUAL
                        ? PermissionEnum.INDIVIDUAL_CUSTOMERS_UPDATE_STATUS_ACTION
                        : PermissionEnum.CORPORATE_CUSTOMERS_UPDATE_STATUS_ACTION,
                    ) && (
                      <ButtonSecondary
                        text="Restrict customer"
                        className="button-white"
                        onClick={() => setRestrictPopupVisible(true)}
                        bold
                      />
                    )}
                    {activePermissions?.includes(
                      customerType === CustomerType.INDIVIDUAL
                        ? PermissionEnum.INDIVIDUAL_CUSTOMERS_CLOSE_ACTION
                        : PermissionEnum.CORPORATE_CUSTOMERS_CLOSE_ACTION,
                    ) && (
                      <ButtonPseudo
                        text="Block customer"
                        className="button-white button-warning"
                        icon={<IconBlock />}
                        onClick={() => setCustomerBlockPopupVisible(true)}
                        bold
                      />
                    )}
                  </div>
                )}
                {/* <button
                  type="button"
                  className={`${cn.bottom} ${
                    menuVisible ? cn.bottomActive : ''
                  }`}
                  onClick={() => setMenuVisibile(!menuVisible)}
                >
                  <IconAttention className={cn.bottomAttentionIcon} />
                  Restrict or block profile
                  {menuVisible ? (
                    <IconUp className={cn.bottomArrowIcon} />
                  ) : (
                    <IconDown className={cn.bottomArrowIcon} />
                  )}
                </button> */}
              </div>
            </ClickOutsideDetector>
          )}
        </div>
      </div>
      <Popup
        title={closeOrganizationPopupTitle}
        description={closeOrganizationPopupDescription}
        popupClassName={cn.closeOrganizationPopup}
        onCancelClick={() => {
          setClosePopupState('warning');
          setClosePopupVisible(false);
          setCloseReason('');
          setOrganizationName('');
          setCloseError('');
        }}
        onOkClick={() => {
          if (closePopupState === 'warning') {
            setClosePopupState('comment');
          } else if (closePopupState === 'comment') {
            setClosePopupState('name');
          } else if (closePopupState === 'name') {
            onCloseOrganizationAction();
          }
        }}
        cancelButtonText="Cancel"
        okButtonText={
          isLoading ? 'Loading...' : closeOrganizationPopupOkButtonText
        }
        okButtonDisabled={
          Boolean(
            (closePopupState === 'comment' && !closeReason) ||
            (closePopupState === 'name' && organizationName !== name),
          ) || isLoading
        }
        isVisible={closePopupVisible}
        warning
      >
        <>
          {closePopupState === 'comment' && (
            <Textarea
              name="reason"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="Reason"
              labelClassName={cn.reason}
            />
          )}
          {closePopupState === 'name' && (
            <>
              <div className={cn.closePopupNameDescription}>
                You are closing the <strong>«{name}»</strong>&nbsp; customer.
                Enter the name in the field below.
              </div>
              {closeError && <div className={cn.closeError}>{closeError}</div>}
              <InputText
                labelClassName={cn.closePopupOrganizationName}
                name="organizationName"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Organization name"
              />
            </>
          )}
        </>
      </Popup>
      <Popup
        title="You are restricting the client account"
        description="The client profile will be active, but all actions will be blocked"
        onCancelClick={() => setRestrictPopupVisible(false)}
        onOkClick={() => setRestrictPopupVisible(false)}
        cancelButtonText="Cancel"
        okButtonText="Yes, restricting"
        isVisible={resrictPopupVisible}
      />
      <Popup
        title="You are blocked the client account"
        description="The client profile will be blocked and unavailable"
        onCancelClick={() => setCustomerBlockPopupVisible(false)}
        onOkClick={() => setCustomerBlockPopupVisible(false)}
        cancelButtonText="Cancel"
        okButtonText="Yes, block"
        isVisible={customerBlockPopupVisible}
        warning
      />
    </>
  );
};

export default CustomerRightSide;
